import React, {
  FC, useCallback, useEffect, useState,
} from 'react';
import { Calendar, Views } from 'react-big-calendar';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import Button from 'components/base/Button';
import Datepicker from 'components/base/Datepicker';
import Loader from 'components/base/Loader';

import EventAgenda from 'components/calendar/AgendaEvent';
import config from 'config';

import { MasterServiceDtoType } from 'config/apiFunus/generated/data-contracts';
import { useAdvice } from 'hooks/useAdvice';
import { i18n } from 'i18n';
import { ExtendedEvent } from 'models/Event';
import {
  addDays,
  getLocalizer,
  getMonth,
  getYear,
  subtractDays,
  subtractHours,
  getDay,
} from 'utils/dateManager';
import { formatEvents } from 'utils/eventFormatter';
import './index.scss';

const resourceMap = [
  {
    resourceId: MasterServiceDtoType.CEREMONIA,
    resourceTitle: i18n.t(`service.${MasterServiceDtoType.CEREMONIA}`),
  },
  {
    resourceId: MasterServiceDtoType.INCINERACION,
    resourceTitle: i18n.t(`service.${MasterServiceDtoType.INCINERACION}`),
  },
  {
    resourceId: MasterServiceDtoType.INHUMACION,
    resourceTitle: i18n.t(`service.${MasterServiceDtoType.INHUMACION}`),
  },
];

export const AgendaPageWithoutInfo: FC = () => {
  const now = new Date();

  return (
    <Redirect
      from={config.url.agendaNoInfo}
      to={`${config.url.agendaNoInfo}/${getYear(now)}/${getMonth(now) + 1}/${getDay(now)}`}
      exact
    />
  );
};

export const AgendaPage: FC = () => {
  const { month, year, day } = useParams<{year: string; month: string, day: string}>();

  const { events: calendarEvents, eventsLoading: loading, fetchEvents } = useAdvice();

  const [date, setDate] = useState<Date>(new Date(Number(year), Number(month) - 1, Number(day)));
  const [events, setEvents] = useState<ExtendedEvent[]>([]);
  const { t } = useTranslation();
  const culture = i18n.language.substring(0, 2);
  const history = useHistory();

  useEffect(() => {
    setDate(new Date(Number(year), Number(month) - 1, Number(day)));
  }, [day, month, year]);

  const loadDate = useCallback(
    (activeDate) => {
      history.push(`${config.url.agendaNoInfo}/${getYear(activeDate)}/${getMonth(activeDate) + 1}/${getDay(activeDate)}`);
    },
    [history],
  );

  const addMonth = () => {
    loadDate(addDays(date, 1));
  };

  const subtractMonth = () => {
    loadDate(subtractDays(date, 1));
  };

  useEffect(
    () => {
      fetchEvents(
        date, [
          MasterServiceDtoType.CEREMONIA,
          MasterServiceDtoType.INCINERACION,
          MasterServiceDtoType.INHUMACION,
        ],
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date],
  );

  useEffect(() => {
    setEvents(formatEvents(calendarEvents));
  }, [calendarEvents]);

  return (
    <div className="agenda-page">
      {loading && <Loader />}
      <div className="agenda-toolbar">
        <Button
          leftAddon={<ChevronLeft />}
          text={t('common.prev')}
          onClick={subtractMonth}
        />
        <Datepicker
          name="date"
          placeholder={t('common.date')}
          selected={date}
          onChange={(dt: Date) => {
            loadDate(dt);
          }}
        />
        <Button
          rightAddon={<ChevronRight />}
          text={t('common.next')}
          onClick={addMonth}
        />
      </div>
      <Calendar
        components={{
          event: EventAgenda,
        }}
        culture={culture}
        date={date}
        defaultView={Views.DAY}
        events={events}
        localizer={getLocalizer()}
        resourceIdAccessor="resourceId"
        resourceTitleAccessor="resourceTitle"
        resources={resourceMap}
        scrollToTime={subtractHours(new Date(), 1)}
        selected={date}
        step={60}
        timeslots={1}
        toolbar={false}
        views={{ day: true }}
      />
    </div>
  );
};
